import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  CalendarIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";
import { useStore } from "@/store/useStore";
import Card from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { motion } from "framer-motion";
import { toast } from "sonner";

const JobDetail = () => {
  const { id } = useParams();
  const { seedData, addApplication } = useStore();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    fullName: "",
    nationalId: "",
    birthDate: "",
    maritalStatus: "",
    gender: "",
    religion: "",
    medicalStatus: "سليم",
    isWarInjured: false,
    injuryDetails: "",
    contactInfo: {
      phone: "",
      governorate: "",
      center: "",
      address: "",
    },
    education: {
      degree: "",
      university: "",
    },
    experience: [
      {
        company: "",
        role: "",
        years: "",
      },
    ],
    totalExperienceYears: 0,
    skills: [],
    newSkill: "",
    militaryService: {
      status: "",
      conductGrade: "",
      serviceYear: "",
    },
    email: "",
    coverLetter: "",
  });
  const [cvFile, setCvFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableCenters, setAvailableCenters] = useState([]);

  const steps = [
    { number: 1, title: "البيانات الشخصية", icon: "👤" },
    { number: 2, title: "معلومات الاتصال", icon: "📞" },
    { number: 3, title: "التعليم والمهارات", icon: "🎓" },
    { number: 4, title: "الخبرة العملية", icon: "💼" },
    { number: 5, title: "الخدمة العسكرية", icon: "🎖️" },
    { number: 6, title: "المستندات", icon: "📎" },
  ];

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://nspo-backend-gray.vercel.app/api/jobs/${id}`);

        if (response.ok) {
          const jobData = await response.json();
          setJob(jobData);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error("Error fetching job from API:", error);

        if (seedData?.jobs) {
          const localJob = seedData.jobs.find(
            (j) => String(j.id) === String(id) || String(j._id) === String(id)
          );
          if (localJob) setJob(localJob);
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchJob();
  }, [id, seedData]);

  useEffect(() => {
    if (formData.contactInfo.governorate && seedData?.governorates) {
      const selectedGovernorate = seedData.governorates.find(
        (gov) => gov.name === formData.contactInfo.governorate
      );
      if (selectedGovernorate) {
        setAvailableCenters(selectedGovernorate.centers);        handleNestedInputChange("contactInfo", "center", "");
      } else {
        setAvailableCenters([]);
      }
    } else {
      setAvailableCenters([]);
    }
  }, [formData.contactInfo.governorate, seedData]);

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.fullName.trim())
          newErrors.fullName = "الاسم الكامل مطلوب";
        if (!formData.nationalId.trim())
          newErrors.nationalId = "الرقم القومي مطلوب";
        if (!formData.birthDate) newErrors.birthDate = "تاريخ الميلاد مطلوب";
        if (!formData.gender) newErrors.gender = "الجنس مطلوب";
        break;

      case 2:
        if (!formData.contactInfo.phone.trim())
          newErrors.phone = "رقم الهاتف مطلوب";
        if (!formData.contactInfo.governorate.trim())
          newErrors.governorate = "المحافظة مطلوبة";
        if (!formData.contactInfo.address.trim())
          newErrors.address = "العنوان مطلوب";
        if (!formData.email.trim()) newErrors.email = "البريد الإلكتروني مطلوب";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
          newErrors.email = "بريد إلكتروني غير صالح";
        break;

      case 3:
        if (!formData.education.degree.trim())
          newErrors.degree = "المؤهل العلمي مطلوب";
        if (!formData.education.university.trim())
          newErrors.university = "اسم الجامعة مطلوب";
        if (formData.skills.length === 0)
          newErrors.skills = "يجب إضافة مهارة واحدة على الأقل";
        break;

      case 4:
        if (
          formData.experience.some(
            (exp) => !exp.company.trim() || !exp.role.trim() || !exp.years
          )
        ) {
          newErrors.experience = "جميع حقول الخبرة مطلوبة";
        }
        break;

      case 5:
        if (!formData.militaryService.status)
          newErrors.militaryStatus = "حالة التجنيد مطلوبة";
        break;

      case 6:
        if (!formData.coverLetter.trim())
          newErrors.coverLetter = "رسالة التغطية مطلوبة";
        if (!cvFile) newErrors.cv = "الرجاء رفع السيرة الذاتية";
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(6)) return;

    setIsSubmitting(true);

    try {
      const totalExp = formData.experience.reduce(
        (sum, exp) => sum + (parseInt(exp.years) || 0),
        0
      );

      const formDataToSend = new FormData();

      formDataToSend.append("fullName", formData.fullName);
      formDataToSend.append("nationalId", formData.nationalId);
      formDataToSend.append("birthDate", formData.birthDate);
      formDataToSend.append("maritalStatus", formData.maritalStatus);
      formDataToSend.append("gender", formData.gender);
      formDataToSend.append("religion", formData.religion);
      formDataToSend.append("medicalStatus", formData.medicalStatus);
      formDataToSend.append("isWarInjured", formData.isWarInjured.toString());
      formDataToSend.append("injuryDetails", formData.injuryDetails);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("coverLetter", formData.coverLetter);
      formDataToSend.append("totalExperienceYears", totalExp.toString());

      // معلومات الاتصال
      formDataToSend.append("governorate", formData.contactInfo.governorate);
      formDataToSend.append("center", formData.contactInfo.center);
      formDataToSend.append("address", formData.contactInfo.address);
      formDataToSend.append("phone", formData.contactInfo.phone);

      // التعليم
      formDataToSend.append("education[degree]", formData.education.degree);
      formDataToSend.append(
        "education[university]",
        formData.education.university
      );

      // الخدمة العسكرية
      formDataToSend.append(
        "militaryService[status]",
        formData.militaryService.status
      );
      formDataToSend.append(
        "militaryService[conductGrade]",
        formData.militaryService.conductGrade
      );
      formDataToSend.append(
        "militaryService[serviceYear]",
        formData.militaryService.serviceYear
      );

      // المهارات
      formData.skills.forEach((skill, index) => {
        formDataToSend.append(`skills[${index}]`, skill);
      });

      // الخبرة العملية
      formData.experience.forEach((exp, index) => {
        formDataToSend.append(`experience[${index}][company]`, exp.company);
        formDataToSend.append(`experience[${index}][role]`, exp.role);
        formDataToSend.append(`experience[${index}][years]`, exp.years);
      });

      // معلومات الوظيفة
      formDataToSend.append("appliedJobs", id);
      formDataToSend.append("jobId", id);
      formDataToSend.append("applicationDate", new Date().toISOString());
      formDataToSend.append("jobTitle", job?.title || job?.title_ar || "وظيفة");
      formDataToSend.append("status", "pending");

      // ملف السيرة الذاتية
      if (cvFile) {
        formDataToSend.append("cv", cvFile);
      }

      const response = await fetch(
        `https://nspo-backend-gray.vercel.app/api/applicants/upload`,
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Application submitted successfully:", result);

        // حفظ في الـ store المحلي
        addApplication({
          jobId: id,
          jobTitle: job?.title || job?.title_ar,
          ...formData,
          cvFile: cvFile?.name,
          applicationDate: new Date().toISOString(),
        });

        toast.success("🎉 تم إرسال الطلب بنجاح!", {
          description: "سيتم مراجعة طلبك قريباً",
          duration: 5000,
        });
        navigate("/careers");
      } else {
        const errorText = await response.text();
        console.error("Server response error:", errorText);
        throw new Error(errorText || "فشل في إرسال الطلب");
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("❌ فشل في إرسال الطلب", {
        description: error.message,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));

    // مسح الخطأ عند تغيير القيمة
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleGovernorateChange = (governorateName) => {
    handleNestedInputChange("contactInfo", "governorate", governorateName);

    // عرض رسالة تأكيد
    if (governorateName) {
      toast.success(`تم اختيار محافظة ${governorateName}`, {
        duration: 2000,
      });
    }
  };

  const handleCenterChange = (centerName) => {
    handleNestedInputChange("contactInfo", "center", centerName);

    // عرض رسالة تأكيد
    if (centerName) {
      toast.success(`تم اختيار مركز ${centerName}`, {
        duration: 2000,
      });
    }
  };

  const addExperience = () => {
    setFormData((prev) => ({
      ...prev,
      experience: [...prev.experience, { company: "", role: "", years: "" }],
    }));
    toast.info("تم إضافة خبرة جديدة", {
      duration: 2000,
    });
  };

  const removeExperience = (index) => {
    setFormData((prev) => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index),
    }));
    toast.info("تم حذف الخبرة", {
      duration: 2000,
    });
  };

  const updateExperience = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      experience: prev.experience.map((exp, i) =>
        i === index ? { ...exp, [field]: value } : exp
      ),
    }));
  };

  const addSkill = () => {
    if (formData.newSkill.trim()) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, formData.newSkill.trim()],
        newSkill: "",
      }));
      toast.success(`تم إضافة مهارة: ${formData.newSkill.trim()}`, {
        duration: 2000,
      });
    }
  };

  const removeSkill = (index) => {
    const skillToRemove = formData.skills[index];
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index),
    }));
    toast.info(`تم حذف مهارة: ${skillToRemove}`, {
      duration: 2000,
    });
  };

  const handleFileUpload = (file) => {
    setCvFile(file);
    if (errors.cv) {
      setErrors((prev) => ({ ...prev, cv: null }));
    }
    toast.success("تم رفع الملف بنجاح", {
      description: file.name,
      duration: 3000,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جارٍ تحميل بيانات الوظيفة...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          الوظيفة غير موجودة
        </h1>
        <p className="text-gray-600 mb-4">تعذر العثور على الوظيفة المطلوبة</p>
        <Button as={Link} to="/careers">
          رجوع لقائمة الوظائف
        </Button>
      </div>
    );
  }

  // عرض الخطوات
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 border-r-4 border-blue-600 pr-3">
              البيانات الشخصية
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="الاسم الكامل *"
                value={formData.fullName}
                onChange={(e) => handleInputChange("fullName", e.target.value)}
                error={errors.fullName}
                required
              />
              <Input
                label="الرقم القومي *"
                value={formData.nationalId}
                onChange={(e) =>
                  handleInputChange("nationalId", e.target.value)
                }
                error={errors.nationalId}
                required
              />
              <Input
                label="تاريخ الميلاد *"
                type="date"
                value={formData.birthDate}
                onChange={(e) => handleInputChange("birthDate", e.target.value)}
                error={errors.birthDate}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الجنس *
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                  required
                >
                  <option value="">اختر الجنس</option>
                  <option value="male">ذكر</option>
                  <option value="female">أنثى</option>
                </select>
                {errors.gender && (
                  <p className="text-sm text-red-600 mt-1">{errors.gender}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الحالة الاجتماعية
                </label>
                <select
                  value={formData.maritalStatus}
                  onChange={(e) =>
                    handleInputChange("maritalStatus", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                >
                  <option value="">اختر الحالة الاجتماعية</option>
                  <option value="single">أعزب</option>
                  <option value="married">متزوج</option>
                  <option value="divorced">مطلق</option>
                  <option value="widowed">أرمل</option>
                </select>
              </div>
              <Input
                label="الديانة"
                value={formData.religion}
                onChange={(e) => handleInputChange("religion", e.target.value)}
              />
            </div>

            {/* الحالة الطبية */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الحالة الطبية
                </label>
                <select
                  value={formData.medicalStatus}
                  onChange={(e) =>
                    handleInputChange("medicalStatus", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                >
                  <option value="سليم">سليم</option>
                  <option value="مريض">مريض</option>
                  <option value="ذوي إعاقة">ذوي إعاقة</option>
                </select>
              </div>

              {/* هل لك موقف طبي؟ */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  هل لك موقف طبي (إصابة حرب، إعاقة، إلخ)؟
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="isWarInjured"
                      checked={formData.isWarInjured === true}
                      onChange={() => handleInputChange("isWarInjured", true)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-600"
                    />
                    <span className="text-gray-700">نعم</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="isWarInjured"
                      checked={formData.isWarInjured === false}
                      onChange={() => handleInputChange("isWarInjured", false)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-600"
                    />
                    <span className="text-gray-700">لا</span>
                  </label>
                </div>

                {/* حقل تفاصيل الموقف الطبي - يظهر فقط إذا كانت الإجابة "نعم" */}
                {formData.isWarInjured && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      تفاصيل الموقف الطبي *
                    </label>
                    <textarea
                      value={formData.injuryDetails}
                      onChange={(e) =>
                        handleInputChange("injuryDetails", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                      rows={3}
                      placeholder="يرجى وصف الموقف الطبي بالتفصيل..."
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 border-r-4 border-blue-600 pr-3">
              معلومات الاتصال
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="رقم الهاتف *"
                type="tel"
                value={formData.contactInfo.phone}
                onChange={(e) =>
                  handleNestedInputChange(
                    "contactInfo",
                    "phone",
                    e.target.value
                  )
                }
                error={errors.phone}
                required
              />
              <Input
                label="البريد الإلكتروني *"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                error={errors.email}
                required
              />

              {/* المحافظة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المحافظة *
                </label>
                <select
                  value={formData.contactInfo.governorate}
                  onChange={(e) => handleGovernorateChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                  required
                >
                  <option value="">اختر المحافظة</option>
                  {seedData?.governorates?.map((gov) => (
                    <option key={gov.name} value={gov.name}>
                      {gov.name}
                    </option>
                  ))}
                </select>
                {errors.governorate && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.governorate}
                  </p>
                )}
              </div>

              {/* المركز */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المركز
                </label>
                <select
                  value={formData.contactInfo.center}
                  onChange={(e) => handleCenterChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                  disabled={!formData.contactInfo.governorate}
                >
                  <option value="">
                    {formData.contactInfo.governorate
                      ? "اختر المركز"
                      : "اختر المحافظة أولاً"}
                  </option>
                  {availableCenters.map((center) => (
                    <option key={center} value={center}>
                      {center}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                العنوان التفصيلي *
              </label>
              <textarea
                value={formData.contactInfo.address}
                onChange={(e) =>
                  handleNestedInputChange(
                    "contactInfo",
                    "address",
                    e.target.value
                  )
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                rows={3}
                required
              />
              {errors.address && (
                <p className="text-sm text-red-600 mt-1">{errors.address}</p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 border-r-4 border-blue-600 pr-3">
              التعليم والمهارات
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="المؤهل العلمي *"
                value={formData.education.degree}
                onChange={(e) =>
                  handleNestedInputChange("education", "degree", e.target.value)
                }
                error={errors.degree}
                required
              />
              <Input
                label="الجامعة *"
                value={formData.education.university}
                onChange={(e) =>
                  handleNestedInputChange(
                    "education",
                    "university",
                    e.target.value
                  )
                }
                error={errors.university}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                المهارات *
              </label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={formData.newSkill}
                  onChange={(e) =>
                    handleInputChange("newSkill", e.target.value)
                  }
                  placeholder="أضف مهارة جديدة"
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={addSkill}
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-6"
                >
                  إضافة
                </Button>
              </div>
              {errors.skills && (
                <p className="text-sm text-red-600 mb-2">{errors.skills}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="bg-blue-100 text-blue-800 px-3 py-2 rounded-xl text-sm flex items-center gap-2 border border-blue-200"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(index)}
                      className="text-red-500 hover:text-red-700 text-lg font-bold"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 border-r-4 border-blue-600 pr-3">
              الخبرة العملية
            </h3>
            {errors.experience && (
              <p className="text-sm text-red-600 bg-red-50 p-3 rounded-xl">
                {errors.experience}
              </p>
            )}

            {formData.experience.map((exp, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl p-4 space-y-4 bg-gray-50"
              >
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-900">
                    خبرة {index + 1}
                  </h4>
                  {formData.experience.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeExperience(index)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      حذف
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="اسم الشركة *"
                    value={exp.company}
                    onChange={(e) =>
                      updateExperience(index, "company", e.target.value)
                    }
                    required
                  />
                  <Input
                    label="المسمى الوظيفي *"
                    value={exp.role}
                    onChange={(e) =>
                      updateExperience(index, "role", e.target.value)
                    }
                    required
                  />
                  <Input
                    label="عدد السنوات *"
                    type="number"
                    value={exp.years}
                    onChange={(e) =>
                      updateExperience(index, "years", e.target.value)
                    }
                    required
                  />
                </div>
              </div>
            ))}

            <Button
              type="button"
              onClick={addExperience}
              variant="outline"
              className="w-full border-dashed border-2 border-gray-300 hover:border-blue-600 hover:bg-blue-50"
            >
              + إضافة خبرة أخرى
            </Button>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 border-r-4 border-blue-600 pr-3">
              الخدمة العسكرية
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  حالة التجنيد *
                </label>
                <select
                  value={formData.militaryService.status}
                  onChange={(e) =>
                    handleNestedInputChange(
                      "militaryService",
                      "status",
                      e.target.value
                    )
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                  required
                >
                  <option value="">اختر الحالة</option>
                  <option value="مؤجل">مؤجل</option>
                  <option value="معافى">معافى</option>
                  <option value="أدى الخدمة">أدى الخدمة</option>
                  <option value="غير مطلوب">غير مطلوب</option>
                </select>
                {errors.militaryStatus && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.militaryStatus}
                  </p>
                )}
              </div>
              <Input
                label="سنة التجنيد"
                type="number"
                value={formData.militaryService.serviceYear}
                onChange={(e) =>
                  handleNestedInputChange(
                    "militaryService",
                    "serviceYear",
                    e.target.value
                  )
                }
              />
              <Input
                label="التقدير"
                value={formData.militaryService.conductGrade}
                onChange={(e) =>
                  handleNestedInputChange(
                    "militaryService",
                    "conductGrade",
                    e.target.value
                  )
                }
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 border-r-4 border-blue-600 pr-3">
              إرفاق المستندات
            </h3>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                رسالة التغطية *
              </label>
              <textarea
                rows={6}
                value={formData.coverLetter}
                onChange={(e) =>
                  handleInputChange("coverLetter", e.target.value)
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200"
                placeholder="اكتب لماذا ترغب في هذه الوظيفة وما الذي يميزك..."
                required
              />
              {errors.coverLetter && (
                <p className="text-sm text-red-600">{errors.coverLetter}</p>
              )}
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-600 transition-all duration-200">
              <DocumentArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <label className="block text-sm font-medium text-gray-700 mb-2">
                السيرة الذاتية *
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileUpload(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
              />
              {cvFile && (
                <p className="text-sm text-green-600 mt-2 bg-green-50 p-2 rounded-lg">
                  ✓ تم اختيار الملف: {cvFile.name}
                </p>
              )}
              {errors.cv && (
                <p className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded-lg">
                  {errors.cv}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                مسموح بالملفات: PDF, DOC, DOCX (الحد الأقصى: 5MB)
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            as={Link}
            to="/careers"
            className="flex items-center text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-4 py-2 rounded-xl transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5 ml-2 rtl:rotate-180" />
            رجوع للوظائف
          </Button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">
              التقديم على الوظيفة
            </h1>
            <p className="text-gray-600 mt-2">املأ النموذج أدناه لتقديم طلبك</p>
          </div>
          <div className="w-20"></div> {/* Spacer for balance */}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Job Details - Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="xl:col-span-1"
          >
            <Card className="sticky top-8 shadow-lg border border-gray-200 rounded-2xl overflow-hidden bg-white">
              <Card.Header className="bg-blue-600 text-white p-6">
                <h2 className="text-2xl font-bold mb-2">
                  {job.title || job.title_ar}
                </h2>
                <div className="space-y-3 mt-4">
                  <div className="flex items-center text-blue-100">
                    <BuildingOfficeIcon className="h-5 w-5 ml-2" />
                    <span>
                      {job.company ||
                        job.department_ar ||
                        "شركة التقنية الحديثة"}
                    </span>
                  </div>
                  <div className="flex items-center text-blue-100">
                    <MapPinIcon className="h-5 w-5 ml-2" />
                    <span>{job.location || job.location_ar || "القاهرة"}</span>
                  </div>
                  <div className="flex items-center text-blue-100">
                    <CalendarIcon className="h-5 w-5 ml-2" />
                    <span>
                      آخر موعد:{" "}
                      {job.endDate
                        ? new Date(job.endDate).toLocaleDateString("ar-EG")
                        : "غير محدد"}
                    </span>
                  </div>
                </div>
              </Card.Header>
              <Card.Content className="p-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      وصف الوظيفة
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {job.description || job.description_ar}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      المتطلبات
                    </h3>
                    <div className="text-gray-600 text-sm space-y-1">
                      {Array.isArray(job.requirements) ? (
                        job.requirements.map((req, index) => (
                          <div key={index} className="flex items-start">
                            <span className="text-blue-600 ml-2">•</span>
                            <span>{req}</span>
                          </div>
                        ))
                      ) : (
                        <p>{job.requirements || "لا توجد متطلبات محددة"}</p>
                      )}
                    </div>
                  </div>
                </div>
              </Card.Content>
            </Card>
          </motion.div>

          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="xl:col-span-2"
          >
            <Card className="shadow-lg border border-gray-200 rounded-2xl overflow-hidden">
              <Card.Content className="p-8">
                {/* Stepper */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-6">
                    {steps.map((step, index) => (
                      <div
                        key={step.number}
                        className="flex flex-col items-center flex-1 relative"
                      >
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300 ${
                            currentStep >= step.number
                              ? "bg-blue-600 text-white border-blue-600 shadow-lg"
                              : "bg-white text-gray-400 border-gray-300"
                          }`}
                        >
                          {currentStep > step.number ? "✓" : step.icon}
                        </div>
                        <span
                          className={`text-xs mt-2 text-center font-medium ${
                            currentStep >= step.number
                              ? "text-blue-600"
                              : "text-gray-500"
                          }`}
                        >
                          {step.title}
                        </span>

                        {/* Connector line */}
                        {index < steps.length - 1 && (
                          <div
                            className={`absolute top-6 left-1/2 w-full h-0.5 -z-10 transition-all duration-300 ${
                              currentStep > step.number
                                ? "bg-blue-600"
                                : "bg-gray-300"
                            }`}
                          ></div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {renderStepContent()}

                  <div className="flex gap-3 pt-6 border-t border-gray-200">
                    {currentStep > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePrevStep}
                        className="flex-1 py-3 text-lg font-medium border-2 border-gray-300 hover:border-blue-600 hover:bg-blue-50 transition-all duration-200"
                      >
                        السابق
                      </Button>
                    )}

                    {currentStep < steps.length ? (
                      <Button
                        type="button"
                        onClick={handleNextStep}
                        className="flex-1 py-3 text-lg font-medium bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        التالي
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        className="flex-1 py-3 text-lg font-medium bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            جاري الإرسال...
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <DocumentArrowUpIcon className="h-5 w-5 ml-2" />
                            إرسال الطلب
                          </div>
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              </Card.Content>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
